<template>
    <ConfigureUserModal
        v-model="input"
        :rp-name="rpName"
        :submitting="submitting"
        :sso-role-warning="ssoRoleWarning"
        :description="modalDescription"
        open
        title="Invite new user to this account"
        submit-label="Send Invitation"
        @submit="onInviteRequest"
        @close="$emit('abort')"
    />
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { RbacRoleDetails } from '@evidentid/tprm-portal-lib/models/rbac/RbacRoleDetails.model';
    import { toRbacRole } from '@evidentid/tprm-portal-lib/utils/common/toRbacRole';
    import delay from '@evidentid/universal-framework/delay';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfigureUserModal from '../components/ConfigureUserModal.vue';
    import { UserInput } from '../types';
    import { useUserManagementStore } from '@/modules/user-management/store';
    import { useSsoProvisioningStore } from '@/modules/sso-provisioning/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: {
            ConfigureUserModal,
        },
        emits: [ 'abort', 'finish' ],
    })
    export default class InviteUserProcedure extends Vue {
        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String })
        private rpName!: string;

        @Prop({ type: String, default: null })
        private rpDisplayName!: string | null;
        // TODO[PRODUCT-24516]: use a proper fix instead of delay
        private delayLoading = false;

        private input: UserInput = {
            name: '',
            email: '',
            role: toRbacRole('viewer'),
        };

        private modalDescription: string =
            'Enter the email address, name and select the role of the new user you would like to add to your account.';

        private get ssoRoleWarning(): boolean {
            return useSsoProvisioningStore().view[this.rpName]?.dynamicCreationEnabled === true;
        }

        private get roles(): RbacRoleDetails[] {
            return useRbacStore().userRoles?.list || [];
        }

        private get status(): { status: OperationStatus, error: any } {
            return useUserManagementStore().inviteUserStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                error: null,
            };
        }

        private get submitting() {
            return this.status.status === OperationStatus.loading || this.delayLoading;
        }

        private success(message: string): void {
            useSnackbarStore().displaySnackbar({ message, success: true });
        }

        private error(message: string): void {
            useSnackbarStore().displaySnackbar({ message, success: false });
        }

        private async onInviteRequest(input: UserInput): Promise<void> {
            if (this.status.status === OperationStatus.loading) {
                return;
            }

            await useUserManagementStore().inviteUser({
                rpName: this.rpName,
                rpDisplayName: this.rpDisplayName,
                input,
            });
            // TODO[PRODUCT-24516]: use a proper fix instead of delay
            this.delayLoading = true;
            await delay(1000);
            this.delayLoading = false;

            const { status, error } = this.status;
            if (status === OperationStatus.success) {
                this.success(`Invite to ${input.email} was sent`);
                this.$emit('finish');
            } else if (error?.response?.message?.includes?.('already exists')) {
                this.error('This user already exists in this account.');
                this.$emit('finish');
            } else if (error?.response?.message?.includes?.('Not permitted to assign role')) {
                this.error(`
                    This action is not allowed since the user has multiple accounts with Evident.
                    Please reach out to Evident support to invite this user.
                `);
            } else {
                this.error('Oops, something went wrong. Please try again!');
            }
        }
    }
</script>
